import { clamp, keyBy, sortBy } from 'lodash';

import {
  AudioEditDetails,
  AudioEditSlice,
  RedactedWords,
  TranscriptionViewableWords,
} from '@common-modules/mediaDetails/models';
import { TRANSCRIPTION_ELLIPSIS_MIN_DURATION_MS } from '@helpers/constants';
import { isGapWord } from './Display/transcriptGap';

/**
 * A position in the media as a clock reading: `m:ss`, or `h:mm:ss` past an hour.
 *
 * Deliberately neither of the two formatters already in the codebase.
 * Timeline's `formatTime` formats seconds to N decimal places, and its
 * zero-decimal path mis-slices a two-digit second — `formatTime(34567, 0)`
 * returns `0:035`. `msToTimeString` below always appends unpadded milliseconds
 * (`01:23.7`), which is right for a word's duration and wrong for a position on
 * a ruler.
 */
export function formatClockTime(ms: number) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`
    : `${minutes}:${seconds}`;
}

export function msToTimeString(duration: number) {
  const milliseconds = duration % 1000 | 0;
  const seconds = (duration / 1000) % 60 | 0;
  const minutes = (duration / (1000 * 60)) % 60 | 0;
  const hours = (duration / (1000 * 60 * 60)) % 24 | 0;

  const hrs = hours < 10 ? '0' + hours : hours;
  const min = minutes < 10 ? '0' + minutes : minutes;
  const sec = seconds < 10 ? '0' + seconds : seconds;

  let timeString = min + ':' + sec + '.' + milliseconds;
  if (hours) {
    timeString = hrs + ':' + timeString;
  }
  return timeString;
}

/**
 * Finds `term` in the laid-out transcript, returning one id-keyed record per match.
 *
 * The row a match sits on is NOT returned: callers resolve it from the current lines (see
 * TranscriptionView's wordIdToRow), so a result still scrolls to the right row after a resize
 * re-breaks the lines.
 *
 * Searches the TRANSCRIPT, not the rendered rows. It used to take the broken lines and never
 * looked past the end of one, so a multi-word phrase that happened to wrap was never found —
 * and because the breaks depend on the panel's width, the same search returned different
 * counts at different widths. Harmless while every match was walked one at a time; not
 * harmless once a button says "Redact all" over a count, which is a claim of completeness.
 * A long gap stays a barrier, explicitly (see isGapWord below) — it was one before only
 * because it owned a row.
 */
export const searchV2 = (
  term: string,
  words: ReadonlyArray<TranscriptionViewableWords>,
  startsWithMatch = true
): Record<string, TranscriptionViewableWords>[] => {
  const FREE_WORDS = ['.', '...'];
  const results: Record<string, TranscriptionViewableWords>[] = [];

  const terms = term
    .trim()
    .split(' ')
    .filter((t) => t !== '')
    .map((t) => t.toLowerCase());

  if (terms.length === 0) {
    return [];
  }

  for (let i = 0; i < words.length; i++) {
    const result: TranscriptionViewableWords[] = [];
    let addResult = true;
    for (let j = 0; j < terms.length; j++) {
      const word = words[i];
      const t = terms[j]!; // Safe due to index conditions
      if (
        word?.words
          .split(/\s*(?:[ .,;])\s*/) // Support editing of transcripts in Illuminate
          .some((w) =>
            startsWithMatch
              ? w.toLowerCase().startsWith(t)
              : w.toLowerCase() === t
          )
      ) {
        result.push(word);
        i++;
      } else if (
        j > 0 &&
        word &&
        // A long gap is a barrier, not something a phrase steps over. It used
        // to be one by accident — it owns a rendered row, and this loop could
        // not cross a row — so keeping it explicit preserves the behaviour now
        // that rows are gone from the search.
        !isGapWord(word) &&
        (FREE_WORDS.includes(word.words) || word.isSpacer)
      ) {
        result.push(word);
        j--;
        i++;
      } else {
        addResult = false;
        break;
      }
    }
    if (addResult) {
      results.push(keyBy(result, 'id'));
    }
  }

  return results;
};

export const search = (
  term: string,
  transcription: ReadonlyArray<TranscriptionViewableWords>,
  startsWithMatch = true
): ReadonlyArray<Record<string, TranscriptionViewableWords>> => {
  const FREE_WORDS = ['.', '...'];
  const results: Record<string, TranscriptionViewableWords>[] = [];
  const terms = term
    .trim()
    .split(' ')
    .filter((t) => t !== '');
  if (terms.length === 0) {
    return [];
  }

  for (let i = 0; i < transcription.length; i++) {
    const result: TranscriptionViewableWords[] = [];
    let addResult = true;
    for (let j = 0; j < terms.length; j++) {
      const word = transcription[i];
      const t = terms[j]!.toLowerCase(); // Safe due to index conditions
      if (
        word &&
        (startsWithMatch
          ? word.words.toLowerCase().startsWith(t)
          : word.words.toLowerCase() === t)
      ) {
        result.push(word);
        i++;
      } else if (
        j > 0 &&
        word &&
        (FREE_WORDS.includes(word.words) || word.isSpacer)
      ) {
        result.push(word);
        j--;
        i++;
      } else {
        addResult = false;
        break;
      }
    }
    if (addResult) {
      results.push(keyBy(result, 'id'));
    }
  }

  return results;
};

export const findContiguousWords = (
  transcription: ReadonlyArray<TranscriptionViewableWords>,
  word: TranscriptionViewableWords,
  pred: (
    word: TranscriptionViewableWords | undefined
  ) => word is TranscriptionViewableWords
): ReadonlyArray<TranscriptionViewableWords> => {
  const oi = transcription.indexOf(word);
  const words: TranscriptionViewableWords[] = [word];
  if (oi !== -1) {
    let li = oi - 1;
    let ri = oi + 1;
    let liWord = transcription[li];
    let riWord = transcription[ri];
    while (pred(liWord) || pred(riWord)) {
      if (pred(liWord)) {
        words.push(liWord);
        li--;
      } else {
        li = -1;
      }
      if (pred(riWord)) {
        words.push(riWord);
        ri++;
      } else {
        ri = transcription.length;
      }

      liWord = transcription[li];
      riWord = transcription[ri];
    }
  }
  return sortBy(words, 'startTimeMs');
};

/**
 * Find transcription words that are effected by audio edits
 */
export function findTranscriptionEditedWords(
  transcription: readonly TranscriptionViewableWords[] | undefined,
  audioEdits: AudioEditSlice[] | undefined,
  mediaDuration?: number
) {
  const redacted: RedactedWords[] = [];
  if (transcription && audioEdits) {
    // since audioRedactionEdits edits and transcription are sorted by time can write this double loop efficiently
    let wordIndex = 0;
    for (const [start, end, editDetails] of audioEdits) {
      const overlapWords: TranscriptionViewableWords[] = [];

      let editSliceMaxStartBuffer = undefined;
      let editSliceMaxStopBuffer = undefined;

      for (let ii = wordIndex; ii < transcription.length; ii++) {
        const word = transcription[ii]!;

        let prevWordIndex = ii - 1;

        let prevWord = transcription[ii - 1];
        if (prevWord && prevWord?.stopTimeMs === prevWord?.startTimeMs) {
          // ignore punctuation
          prevWordIndex -= 1;
          prevWord = transcription[prevWordIndex];
        }

        if (
          prevWord &&
          prevWord?.isSpacer &&
          prevWord.stopTimeMs - prevWord.startTimeMs <
            TRANSCRIPTION_ELLIPSIS_MIN_DURATION_MS
        ) {
          prevWordIndex -= 1;
          prevWord = transcription[prevWordIndex];

          if (prevWord && prevWord?.stopTimeMs === prevWord?.startTimeMs) {
            // ignore punctuation
            prevWordIndex -= 1;
            prevWord = transcription[prevWordIndex];
          }
        }

        const wordIsHiddenSpacer =
          word.isSpacer &&
          word.stopTimeMs - word.startTimeMs <
            TRANSCRIPTION_ELLIPSIS_MIN_DURATION_MS;

        // if word extends past slice then we should set the editSliceMaxStopBuffer
        if (
          word.stopTimeMs > end &&
          prevWord &&
          !editSliceMaxStopBuffer &&
          !wordIsHiddenSpacer
        ) {
          // percentage based max (not being used)
          // editSliceMaxStopBuffer = Math.floor(
          //   word.startTimeMs +
          //     (word.stopTimeMs - word.startTimeMs) * 0.75 -
          //     prevWord.stopTimeMs
          editSliceMaxStopBuffer = word.stopTimeMs - prevWord.stopTimeMs - 1; // allow buffer all but last ms of adjacent word - so will remain a partial redaction when using a buffer
        }

        // past edit span so break
        if (word.startTimeMs >= end && word.stopTimeMs > end) {
          if (wordIsHiddenSpacer) {
            continue;
          }
          wordIndex = ii;
          break;
        }

        //
        if (ii === transcription.length - 1 && mediaDuration) {
          editSliceMaxStopBuffer = Math.max(
            0,
            mediaDuration * 1000 - word.stopTimeMs
          );
        }

        // check if word overlaps slice at all
        const overlap =
          Math.min(word.stopTimeMs, end) - Math.max(word.startTimeMs, start);
        if (
          overlap >= 1 ||
          (overlap === 0 &&
            word.startTimeMs === word.stopTimeMs &&
            word.startTimeMs > start)
        ) {
          // must overlap, also allow for punctuation after start
          overlapWords.push(word);

          if (
            !editSliceMaxStartBuffer &&
            word.startTimeMs >= start &&
            !wordIsHiddenSpacer
          ) {
            if (prevWord) {
              // percentage based max (not being used)
              // editSliceMaxStartBuffer = Math.floor(
              //   word.startTimeMs -
              //     (prevWord.stopTimeMs -
              //       (prevWord.stopTimeMs - prevWord.startTimeMs) * 0.75)
              // ); // only allow buffer 75% of adjacent word
              editSliceMaxStartBuffer =
                word.startTimeMs - prevWord.startTimeMs - 1; // allow buffer all but last ms of adjacent word - so will remain a partial redaction when using a buffer
            } else {
              editSliceMaxStartBuffer = word.startTimeMs; // first word can buffer to start of file
            }
          }
        }
      }

      // finalize slice
      if (overlapWords.length) {
        // filter out hidden spacers when calculating buffers
        const overlapVisibleWords = overlapWords.filter(
          (word) =>
            !(
              word.isSpacer &&
              word.stopTimeMs - word.startTimeMs <
                TRANSCRIPTION_ELLIPSIS_MIN_DURATION_MS
            )
        );

        // if no words visible then we will continue
        if (overlapVisibleWords.length === 0) {
          // overlapVisibleWords = overlapWords; // in future maybe we will show these in the transcription tab
          continue;
        }

        // --- calculate startBufferMs - will occur is slice doesn't not align with first word start
        let startBufferMs = undefined;
        const firstWord = overlapVisibleWords[0]!;
        const secondWord = overlapVisibleWords[1];

        // slice edit starts prior to first word
        if (firstWord.startTimeMs - start > 0) {
          startBufferMs = firstWord.startTimeMs - start; // calculate buffer relative to first word
        } else if (start > firstWord.startTimeMs && secondWord) {
          // caluculate buffer relative to second word
          startBufferMs = secondWord.startTimeMs - start;
        }

        // --- calculate startBufferMs - will occur is slice doesn't not align with last word end
        let stopBufferMs = undefined;
        const lastWord = overlapVisibleWords.at(-1)!;
        const secondLastWord = overlapVisibleWords.at(-2);

        // slice edit extends past last word
        if (end - lastWord.stopTimeMs > 0) {
          stopBufferMs = end - lastWord.stopTimeMs;
        } else if (end < lastWord.stopTimeMs && secondLastWord) {
          // calculate buffer relative to second to last word
          stopBufferMs = end - secondLastWord.stopTimeMs;
        }

        // add buffer into details
        let updatedDetails: AudioEditDetails;
        if (!editDetails) {
          // dont think we ever would actually hit this condition (maybe with legacy data?)
          // create new details
          updatedDetails = {
            user: '',
            modifiedDateTime: new Date().toISOString(),
            labels: [],
            notes: '',
          };
        } else {
          updatedDetails = {
            ...editDetails,
            startBufferMs,
            stopBufferMs,
          };
        }

        overlapWords.forEach((word) => {
          // calculate percent of word that is "redacted"
          const startRedactionMs = Math.max(word.startTimeMs, start);
          const stopRedactionMs = Math.min(word.stopTimeMs, end);
          const coverage =
            (stopRedactionMs - startRedactionMs + 1) /
            (word.stopTimeMs - word.startTimeMs + 1);

          redacted.push({
            ...word,
            coverage, // percent (0-1) of word that is "redacted/masked"
            // edit slice word is assigned to info
            editSliceMaxStartBuffer, // largest stop buffer to allow
            editSliceMaxStopBuffer, // largest stop buffer to allow
            editSliceStart: start,
            editSliceStop: end,
            editDetails: updatedDetails,
          });
        });
      }
    }
  }
  return redacted;
}

/**
 * Find if currentTime falls within the provided audio edits slices using binary search.
 */
export const isInAudioSlices = (
  slices: Array<AudioEditSlice>,
  currentTime: number
) => {
  let firstIndex = 0;
  let lastIndex = slices.length - 1;
  let middleIndex = clamp(
    Math.floor((lastIndex + firstIndex) / 2),
    0,
    slices.length - 1
  );

  if (lastIndex < 0) {
    return false;
  }

  const compare = (item: AudioEditSlice, time: number) =>
    time >= item[0] && time <= item[1] ? 0 : time < item[0] ? -1 : 1;

  let middleItem = slices[middleIndex]!; // Safe to use ! here due to index calculation
  while (compare(middleItem, currentTime) !== 0 && firstIndex < lastIndex) {
    if (compare(middleItem, currentTime) < 0) {
      lastIndex = middleIndex - 1;
    } else if (compare(middleItem, currentTime) > 0) {
      firstIndex = middleIndex + 1;
    }
    middleIndex = clamp(
      Math.floor((lastIndex + firstIndex) / 2),
      0,
      slices.length - 1
    );
    middleItem = slices[middleIndex]!; // Safe to use ! here due to index calculation
  }

  return compare(middleItem, currentTime) === 0;
};
